import { useMemo, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { getSuggestions } from '@gonfalon/openapi/operations/getSuggestions';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  ComboBox,
  FieldGroup,
  Group,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  Code,
} from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import { RequiredLabel } from '../components/RequiredLabel';
import type { MetricForm } from '../formUtils';

import styles from './NumericSpanAttributeField.module.css';

interface NumericSpanAttributeFieldProps {
  control: Control<MetricForm>;
}

const FIELD_NAME = 'traceValueLocation';

export const NumericSpanAttributeField: React.FC<NumericSpanAttributeFieldProps> = ({ control }) => {
  const projectKey = useProjectKey();
  // Same getSuggestions request shape (and queryKey) as MetricSourceQueryBuilder
  // so the two share react-query cache. No filters — every suggested span
  // attribute is surfaced as an option, with item.field used as label/value.
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  const { data } = useQuery({
    queryFn: async () =>
      getSuggestions({
        body: {
          projectKey,
          dataSource: {
            domain: 'observability',
            kind: 'traces',
            timeRange: {
              from: sevenDaysAgo,
              to: now,
              timezone: 'America/New_York',
            },
          },
          suggestFields: {
            limit: 100,
            withValues: {
              limit: 100,
            },
          },
        },
      }),
    queryKey: ['suggestions', projectKey],
  });
  const options = useMemo(
    () =>
      (data?.data?.items ?? [])
        .map((item) => item.field)
        .filter((f): f is string => Boolean(f))
        .map((field) => ({ id: field, key: field, textValue: field })),
    [data],
  );

  return (
    <Controller
      control={control}
      name={FIELD_NAME}
      rules={{ required: 'Span attribute is required' }}
      shouldUnregister
      render={({ field, fieldState: { error } }) => (
        <FieldGroup errorMessage={error?.message}>
          <RequiredLabel label="Choose a numeric span attribute" htmlFor={FIELD_NAME} />
          <div className={styles.fieldDescription}>
            The numeric span attribute to use as the measured value. ex: <Code>http.response_content_length</Code>
          </div>
          {/*
            `allowsCustomValue` lets the user type any attribute path, even
            ones that aren't in the surfaced suggestion list — the suggestions
            are a convenience for the common cases. ComboBox stores the typed
            string as `inputValue`, which we mirror to react-hook-form on every
            keystroke so the form value tracks free-text edits as well as
            picks from the listbox.
          */}
          <ComboBox
            aria-label="Numeric span attribute"
            allowsCustomValue
            isInvalid={!!error}
            selectedKey={options.some((o) => o.id === field.value) ? (field.value ?? null) : null}
            inputValue={field.value ?? ''}
            onInputChange={(value) => field.onChange(value)}
            onSelectionChange={(key) => {
              if (typeof key === 'string') {
                field.onChange(key);
              }
            }}
          >
            <Group>
              <Input id={FIELD_NAME} placeholder="Select a span attribute" />
              <IconButton
                aria-label="Show numeric span attributes"
                icon="chevron-down"
                size="small"
                variant="minimal"
              />
            </Group>
            <Popover>
              <ListBox items={options} selectionMode="single" aria-label="Numeric span attributes">
                {(item) => (
                  <ListBoxItem key={item.key} id={item.id} textValue={item.textValue}>
                    {item.textValue}
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </ComboBox>
        </FieldGroup>
      )}
    />
  );
};
